// ==================== CONTACT GROUPS MANAGEMENT PAGE ====================

import { useState, useEffect, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FolderOpen,
  Plus,
  Pencil,
  Trash2,
  Loader2,
  Users,
  AlertCircle,
  CheckCircle,
  X,
  Palette,
  FileText,
  Search,
  Archive,
  Eye,
  EyeOff,
  Zap,
  Star,
  MoreVertical,
  Send,
  Download,
  UserCheck,
} from 'lucide-react';
import { Button, Card, Badge, Input, Modal, Select, Checkbox, ConfirmDialog, Dropdown } from '../../../components/ui';
import type { SelectOption } from '../../../components/ui/Select';
import { contactsService } from '../../../services/contacts.service';
import type { ContactGroup, ContactGroupStatus, ContactGroupType, RuleCondition, AutoAddRules, RuleOperator } from '../../../types';
import ViewMembersModal from './ViewMembersModal';
import ExportGroupMembersModal from './ExportGroupMembersModal';

// ==================== COLOR OPTIONS ====================

const COLOR_OPTIONS = [
  { value: '#f97316', label: 'Orange' },
  { value: '#ef4444', label: 'Red' },
  { value: '#ec4899', label: 'Pink' },
  { value: '#a855f7', label: 'Purple' },
  { value: '#6366f1', label: 'Indigo' },
  { value: '#3b82f6', label: 'Blue' },
  { value: '#06b6d4', label: 'Cyan' },
  { value: '#14b8a6', label: 'Teal' },
  { value: '#22c55e', label: 'Green' },
  { value: '#84cc16', label: 'Lime' },
  { value: '#eab308', label: 'Yellow' },
  { value: '#78716c', label: 'Gray' },
];

const STATUS_OPTIONS: SelectOption[] = [
  { value: 'ACTIVE', label: 'Active' },
  { value: 'INACTIVE', label: 'Inactive' },
  { value: 'ARCHIVED', label: 'Archived' },
];

const TYPE_OPTIONS: SelectOption[] = [
  { value: 'STATIC', label: 'Static (Manual)', icon: <Users className="w-4 h-4" /> },
  { value: 'DYNAMIC', label: 'Dynamic (Auto)', icon: <Zap className="w-4 h-4" /> },
];

const FILTER_OPTIONS: SelectOption[] = [
  { value: 'all', label: 'All Groups' },
  { value: 'ACTIVE', label: 'Active Only' },
  { value: 'INACTIVE', label: 'Inactive Only' },
  { value: 'ARCHIVED', label: 'Archived' },
];

// ==================== RULE OPTIONS ====================

const RULE_FIELD_OPTIONS: SelectOption[] = [
  { value: 'country', label: 'Country' },
  { value: 'tags', label: 'Tags' },
  { value: 'optedIn', label: 'Opted In Status' },
  { value: 'company', label: 'Company' },
];

const RULE_OPERATOR_OPTIONS: Record<string, SelectOption[]> = {
  country: [
    { value: 'equals', label: 'equals' },
    { value: 'not_equals', label: 'does not equal' },
    { value: 'contains', label: 'contains' },
    { value: 'in', label: 'is one of' },
  ],
  tags: [
    { value: 'contains', label: 'includes' },
    { value: 'not_contains', label: 'does not include' },
    { value: 'in', label: 'includes any of' },
  ],
  optedIn: [
    { value: 'equals', label: 'is' },
  ],
  company: [
    { value: 'equals', label: 'equals' },
    { value: 'not_equals', label: 'does not equal' },
    { value: 'contains', label: 'contains' },
  ],
};

const OPTED_IN_VALUE_OPTIONS: SelectOption[] = [
  { value: 'true', label: 'Opted In' },
  { value: 'false', label: 'Opted Out' },
];

// ==================== FORM STATE INTERFACE ====================

interface GroupFormData {
  name: string;
  description: string;
  color: string;
  icon: string;
  status: ContactGroupStatus;
  type: ContactGroupType;
  isDefault: boolean;
  autoAddRules: AutoAddRules;
}

const initialAutoAddRules: AutoAddRules = {
  operator: 'AND',
  conditions: [],
};

const initialFormData: GroupFormData = {
  name: '',
  description: '',
  color: '#f97316',
  icon: '',
  status: 'ACTIVE',
  type: 'STATIC',
  isDefault: false,
  autoAddRules: { ...initialAutoAddRules },
};

// ==================== STATUS BADGE COMPONENT ====================

const StatusBadge = ({ status }: { status: ContactGroupStatus }) => {
  const config = {
    ACTIVE: { variant: 'success' as const, label: 'Active', icon: Eye },
    INACTIVE: { variant: 'warning' as const, label: 'Inactive', icon: EyeOff },
    ARCHIVED: { variant: 'default' as const, label: 'Archived', icon: Archive },
  };
  
  const { variant, label, icon: Icon } = config[status];
  
  return (
    <Badge variant={variant} className="flex items-center gap-1 text-xs">
      <Icon className="w-3 h-3" />
      {label}
    </Badge>
  );
};

// ==================== TYPE BADGE COMPONENT ====================

const TypeBadge = ({ type }: { type: ContactGroupType }) => {
  const config = {
    STATIC: { label: 'Static', icon: Users, className: 'bg-blue-500/10 text-blue-400 border-blue-500/30' },
    DYNAMIC: { label: 'Dynamic', icon: Zap, className: 'bg-purple-500/10 text-purple-400 border-purple-500/30' },
  };
  
  const { label, icon: Icon, className } = config[type];
  
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs border ${className}`}>
      <Icon className="w-3 h-3" />
      {label}
    </span>
  );
};

// ==================== GROUP ITEM COMPONENT ====================

interface GroupItemProps {
  group: ContactGroup;
  onEdit: () => void;
  onDelete: () => void;
  onToggleStatus: () => void;
  onViewMembers: () => void;
  onExport: () => void;
}

const GroupItem = ({ group, onEdit, onDelete, onToggleStatus, onViewMembers, onExport }: GroupItemProps) => {
  const memberCount = group._count?.members || 0;
  const optedInCount = group.optedInCount || 0;
  const optedInPercentage = memberCount > 0 ? Math.round((optedInCount / memberCount) * 100) : 0;

  const dropdownItems = [
    {
      key: 'view-members',
      label: 'View Members',
      icon: <Users className="w-4 h-4" />,
      onClick: onViewMembers,
    },
    {
      key: 'edit-group',
      label: 'Edit Group',
      icon: <Pencil className="w-4 h-4" />,
      onClick: onEdit,
    },
    {
      key: 'toggle-status',
      label: group.status === 'ACTIVE' ? 'Deactivate' : 'Activate',
      icon: group.status === 'ACTIVE' ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />,
      onClick: onToggleStatus,
    },
    {
      key: 'send-campaign',
      label: 'Send Campaign',
      icon: <Send className="w-4 h-4" />,
      onClick: () => console.log('Send campaign to group'),
      disabled: memberCount === 0,
    },
    {
      key: 'export-members',
      label: 'Export Members',
      icon: <Download className="w-4 h-4" />,
      onClick: onExport,
      disabled: memberCount === 0,
    },
    {
      key: 'delete-group',
      label: 'Delete Group',
      icon: <Trash2 className="w-4 h-4" />,
      onClick: onDelete,
      colorClass: 'text-red-400 hover:text-red-300',
    },
  ];

  return (
    <div className={`flex items-center gap-4 p-4 transition-colors ${
      group.status === 'ARCHIVED' ? 'opacity-60' : 'hover:bg-white/5'
    }`}>
      {/* Color Indicator */}
      <div
        className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0"
        style={{ backgroundColor: `${group.color || '#f97316'}15` }}
      >
        <FolderOpen
          className="w-6 h-6"
          style={{ color: group.color || '#f97316' }}
        />
      </div>

      {/* Group Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <h4 className="text-sm font-medium text-white truncate">{group.name}</h4>
          <StatusBadge status={group.status} />
          <TypeBadge type={group.type} />
          {group.isDefault && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-yellow-500/10 text-yellow-400 border border-yellow-500/30">
              <Star className="w-3 h-3" />
              Default
            </span>
          )}
        </div>
        
        {group.description && (
          <p className="text-xs text-(--color-muted-gray) mt-1 truncate">
            {group.description}
          </p>
        )}
        
        <div className="flex items-center gap-4 mt-2">
          <span className="text-xs text-(--color-muted-gray) flex items-center gap-1">
            <Users className="w-3 h-3" />
            {memberCount} member{memberCount !== 1 ? 's' : ''}
          </span>
          {memberCount > 0 && (
            <span className="text-xs text-(--color-muted-gray) flex items-center gap-1">
              <UserCheck className="w-3 h-3" />
              {optedInCount} opted-in ({optedInPercentage}%)
            </span>
          )}
          <span className="text-xs text-(--color-muted-gray)">
            Created {new Date(group.createdAt).toLocaleDateString()}
          </span>
        </div>
      </div>

      {/* Opt-in Rate Bar */}
      {memberCount > 0 && (
        <div className="hidden md:block w-24 shrink-0">
          <div className="flex items-center justify-between text-xs mb-1">
            <span className="text-(--color-muted-gray)">Opt-in</span>
            <span className="text-white font-medium">{optedInPercentage}%</span>
          </div>
          <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
            <div
              className="h-full rounded-full bg-linear-to-r from-green-500 to-emerald-400 transition-all"
              style={{ width: `${optedInPercentage}%` }}
            />
          </div>
        </div>
      )}

      {/* Actions Dropdown */}
      <div className="shrink-0">
        <Dropdown
          trigger={<MoreVertical className="w-4 h-4" />}
          items={dropdownItems}
          align="right"
        />
      </div>
    </div>
  );
};

// ==================== MAIN COMPONENT ====================

const GroupsPage = () => {
  // State
  const [groups, setGroups] = useState<ContactGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState<ContactGroup | null>(null);
  const [formData, setFormData] = useState<GroupFormData>(initialFormData);
  const [modalError, setModalError] = useState<string | null>(null);

  // Delete confirmation
  const [deleteConfirm, setDeleteConfirm] = useState<ContactGroup | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Status toggle confirmation
  const [toggleConfirm, setToggleConfirm] = useState<ContactGroup | null>(null);
  const [toggling, setToggling] = useState(false);

  // View members modal
  const [viewingMembersGroup, setViewingMembersGroup] = useState<ContactGroup | null>(null);

  // Export members modal
  const [exportingGroup, setExportingGroup] = useState<ContactGroup | null>(null);

  // ==================== FETCH GROUPS ====================

  const fetchGroups = useCallback(async () => {
    try {
      setLoading(true);
      const response = await contactsService.getGroups();
      setGroups(response || []);
    } catch (err) {
      console.error('Failed to fetch groups:', err);
      setError('Failed to load groups');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  // ==================== FILTERED GROUPS ====================

  const filteredGroups = useMemo(() => {
    return groups.filter((group) => {
      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        if (!group.name.toLowerCase().includes(query) &&
            !group.description?.toLowerCase().includes(query)) {
          return false;
        }
      }
      
      // Status filter
      if (statusFilter !== 'all' && group.status !== statusFilter) {
        return false;
      }
      
      return true;
    });
  }, [groups, searchQuery, statusFilter]);

  // ==================== STATS ====================

  const stats = useMemo(() => {
    const total = groups.length;
    const active = groups.filter((g) => g.status === 'ACTIVE').length;
    const totalMembers = groups.reduce((sum, g) => sum + (g._count?.members || 0), 0);
    const totalOptedIn = groups.reduce((sum, g) => sum + (g.optedInCount || 0), 0);
    
    return { total, active, totalMembers, totalOptedIn };
  }, [groups]);

  // ==================== HANDLERS ====================

  const handleOpenModal = (group?: ContactGroup) => {
    if (group) {
      setEditingGroup(group);
      setFormData({
        name: group.name,
        description: group.description || '',
        color: group.color || '#f97316',
        icon: group.icon || '',
        status: group.status,
        type: group.type,
        isDefault: group.isDefault,
        autoAddRules: group.autoAddRules || { ...initialAutoAddRules },
      });
    } else {
      setEditingGroup(null);
      setFormData(initialFormData);
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingGroup(null);
    setFormData(initialFormData);
    setModalError(null);
  };

  const handleSave = async () => {
    // Validate
    if (!formData.name.trim()) {
      setModalError('Group name is required');
      return;
    }

    // Validate dynamic groups have at least one rule
    if (formData.type === 'DYNAMIC' && formData.autoAddRules.conditions.length === 0) {
      setModalError('Dynamic groups require at least one rule');
      return;
    }

    try {
      setSaving(true);
      setModalError(null);

      const payload = {
        name: formData.name.trim(),
        description: formData.description.trim() || undefined,
        color: formData.color,
        icon: formData.icon || undefined,
        status: formData.status,
        type: formData.type,
        isDefault: formData.isDefault,
        // Only include autoAddRules for dynamic groups
        autoAddRules: formData.type === 'DYNAMIC' ? formData.autoAddRules : undefined,
      };

      if (editingGroup) {
        await contactsService.updateGroup(editingGroup.id, payload);
        setSuccess('Group updated successfully');
      } else {
        await contactsService.createGroup(payload);
        setSuccess('Group created successfully');
      }

      handleCloseModal();
      fetchGroups();

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: unknown) {
      console.error('Failed to save group:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to save group';
      setModalError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;

    try {
      setDeleting(true);
      await contactsService.deleteGroup(deleteConfirm.id);
      setSuccess('Group deleted successfully');
      setDeleteConfirm(null);
      fetchGroups();

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: unknown) {
      console.error('Failed to delete group:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete group';
      setError(errorMessage);
      setDeleteConfirm(null);
    } finally {
      setDeleting(false);
    }
  };

  const handleToggleStatus = (group: ContactGroup) => {
    setToggleConfirm(group);
  };

  const confirmToggleStatus = async () => {
    if (!toggleConfirm) return;

    try {
      setToggling(true);
      const newStatus = toggleConfirm.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';
      await contactsService.updateGroup(toggleConfirm.id, { status: newStatus });
      setSuccess(`Group ${newStatus === 'ACTIVE' ? 'activated' : 'deactivated'} successfully`);
      setToggleConfirm(null);
      fetchGroups();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Failed to toggle status:', err);
      setError('Failed to update group status');
    } finally {
      setToggling(false);
    }
  };

  // ==================== RENDER ====================

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-heading font-bold text-white flex items-center gap-3">
            <FolderOpen className="w-8 h-8 text-primary-400" />
            Contact Groups
          </h1>
          <p className="text-sm text-(--color-muted-gray) mt-1">
            Organize your contacts into groups for targeted messaging and easier management
          </p>
        </div>
        <Button onClick={() => handleOpenModal()} className="shrink-0">
          <Plus className="w-4 h-4 mr-2" />
          Create Group
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-linear-to-br from-orange-500/20 to-orange-600/10 flex items-center justify-center">
              <FolderOpen className="w-5 h-5 text-orange-500" />
            </div>
            <div>
              <p className="text-2xl font-semibold text-white">{stats.total}</p>
              <p className="text-xs text-(--color-muted-gray)">Total Groups</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-linear-to-br from-green-500/20 to-green-600/10 flex items-center justify-center">
              <Eye className="w-5 h-5 text-green-500" />
            </div>
            <div>
              <p className="text-2xl font-semibold text-white">{stats.active}</p>
              <p className="text-xs text-(--color-muted-gray)">Active Groups</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-linear-to-br from-blue-500/20 to-blue-600/10 flex items-center justify-center">
              <Users className="w-5 h-5 text-blue-500" />
            </div>
            <div>
              <p className="text-2xl font-semibold text-white">{stats.totalMembers}</p>
              <p className="text-xs text-(--color-muted-gray)">Total Members</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-linear-to-br from-emerald-500/20 to-emerald-600/10 flex items-center justify-center">
              <UserCheck className="w-5 h-5 text-emerald-500" />
            </div>
            <div>
              <p className="text-2xl font-semibold text-white">{stats.totalOptedIn}</p>
              <p className="text-xs text-(--color-muted-gray)">Opted-In Members</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Filters */}
      <Card className="p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-(--color-muted-gray)" />
            <Input
              placeholder="Search groups..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          <div className="w-48">
            <Select
              options={FILTER_OPTIONS}
              value={statusFilter}
              onChange={(value) => setStatusFilter(value as string)}
              placeholder="Filter by status"
            />
          </div>
        </div>
      </Card>

      {/* Alerts */}
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="flex items-center gap-3 p-4 rounded-lg bg-red-500/10 border border-red-500/30"
          >
            <AlertCircle className="w-5 h-5 text-red-400 shrink-0" />
            <p className="text-sm text-red-300 flex-1">{error}</p>
            <button onClick={() => setError(null)} className="text-red-400 hover:text-red-300">
              <X className="w-4 h-4" />
            </button>
          </motion.div>
        )}

        {success && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="flex items-center gap-3 p-4 rounded-lg bg-green-500/10 border border-green-500/30"
          >
            <CheckCircle className="w-5 h-5 text-green-400 shrink-0" />
            <p className="text-sm text-green-300 flex-1">{success}</p>
            <button onClick={() => setSuccess(null)} className="text-green-400 hover:text-green-300">
              <X className="w-4 h-4" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Loading State */}
      {loading ? (
        <Card className="p-8">
          <div className="flex items-center justify-center gap-3">
            <Loader2 className="w-5 h-5 text-orange-500 animate-spin" />
            <span className="text-(--color-muted-gray)">Loading groups...</span>
          </div>
        </Card>
      ) : filteredGroups.length === 0 ? (
        /* Empty State */
        <Card className="p-8">
          <div className="text-center">
            <div className="w-16 h-16 rounded-2xl bg-linear-to-br from-orange-500/20 to-orange-600/10 flex items-center justify-center mx-auto mb-4">
              <FolderOpen className="w-8 h-8 text-orange-500" />
            </div>
            <h3 className="text-lg font-medium text-white mb-2">
              {groups.length === 0 ? 'No Groups Yet' : 'No Groups Found'}
            </h3>
            <p className="text-sm text-(--color-muted-gray) mb-6 max-w-md mx-auto">
              {groups.length === 0
                ? 'Create groups to organize your contacts by category, campaign, location, or any criteria that makes sense for your business.'
                : 'No groups match your current filters. Try adjusting your search or filter criteria.'}
            </p>
            {groups.length === 0 && (
              <Button onClick={() => handleOpenModal()}>
                <Plus className="w-4 h-4 mr-2" />
                Create Your First Group
              </Button>
            )}
          </div>
        </Card>
      ) : (
        /* Groups List */
        <Card className="overflow-hidden">
          <div className="divide-y divide-(--color-border-gray)">
            {filteredGroups.map((group) => (
              <GroupItem
                key={group.id}
                group={group}
                onEdit={() => handleOpenModal(group)}
                onDelete={() => setDeleteConfirm(group)}
                onToggleStatus={() => handleToggleStatus(group)}
                onViewMembers={() => setViewingMembersGroup(group)}
                onExport={() => setExportingGroup(group)}
              />
            ))}
          </div>
        </Card>
      )}

      {/* Add/Edit Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={editingGroup ? 'Edit Group' : 'Create Group'}
        subtitle={editingGroup ? 'Update group settings' : 'Create a new contact group'}
        size="lg"
        showCloseButton
      >
        <div className="space-y-5 p-6">
          {/* Group Name */}
          <Input
            label="Group Name"
            placeholder="e.g., VIP Customers"
            value={formData.name}
            onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
            required
            leftIcon={<FolderOpen className="w-4 h-4" />}
          />

          {/* Description */}
          <div>
            <label className="flex items-center gap-1.5 text-sm font-medium text-(--color-soft-gray) mb-2">
              <FileText className="w-4 h-4" />
              Description
            </label>
            <textarea
              placeholder="Optional description for this group..."
              value={formData.description}
              onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
              rows={3}
              className="w-full px-4 py-3 rounded-lg bg-(--color-charcoal-black) border border-(--color-border-gray) text-white placeholder:text-(--color-muted-gray) focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500 transition-all resize-none"
            />
          </div>

          {/* Status and Type */}
          <div className="grid grid-cols-2 gap-4">
            <Select
              label="Status"
              options={STATUS_OPTIONS}
              value={formData.status}
              onChange={(value) => setFormData((prev) => ({ ...prev, status: value as ContactGroupStatus }))}
            />
            <Select
              label="Type"
              options={TYPE_OPTIONS}
              value={formData.type}
              onChange={(value) => setFormData((prev) => ({ 
                ...prev, 
                type: value as ContactGroupType,
                // Reset rules when switching to static
                autoAddRules: value === 'STATIC' ? { ...initialAutoAddRules } : prev.autoAddRules,
                // Dynamic groups cannot be default
                isDefault: value === 'DYNAMIC' ? false : prev.isDefault
              }))}
            />
          </div>

          {/* Dynamic Group Rules */}
          {formData.type === 'DYNAMIC' && (
            <div className="p-4 rounded-lg bg-white/5 border border-white/10">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Zap className="w-4 h-4 text-purple-400" />
                  <span className="text-sm font-medium text-white">Auto-Add Rules</span>
                </div>
                <div className="w-40">
                  <Select
                    options={[
                      { value: 'AND', label: 'Match ALL rules' },
                      { value: 'OR', label: 'Match ANY rule' },
                    ]}
                    value={formData.autoAddRules.operator}
                    onChange={(value) => setFormData((prev) => ({
                      ...prev,
                      autoAddRules: { ...prev.autoAddRules, operator: value as 'AND' | 'OR' }
                    }))}
                  />
                </div>
              </div>

              <p className="text-xs text-(--color-muted-gray) mb-4">
                Contacts matching these rules will be automatically added to this group
              </p>

              {/* Rules List */}
              <div className="space-y-3">
                {formData.autoAddRules.conditions.map((condition, index) => (
                  <div key={index} className="flex items-center gap-2 p-3 rounded-lg bg-(--color-charcoal-black) border border-(--color-border-gray)">
                    {/* Field selector */}
                    <div className="w-32 shrink-0">
                      <Select
                        options={RULE_FIELD_OPTIONS}
                        value={condition.field}
                        onChange={(value) => {
                          const newConditions = [...formData.autoAddRules.conditions];
                          newConditions[index] = {
                            ...newConditions[index],
                            field: (Array.isArray(value) ? value[0] : value) as RuleCondition['field'],
                            operator: 'equals' as RuleOperator,
                            value: undefined,
                            values: undefined,
                          };
                          setFormData((prev) => ({
                            ...prev,
                            autoAddRules: { ...prev.autoAddRules, conditions: newConditions }
                          }));
                        }}
                      />
                    </div>

                    {/* Operator selector */}
                    <div className="w-36 shrink-0">
                      <Select
                        options={RULE_OPERATOR_OPTIONS[condition.field] || RULE_OPERATOR_OPTIONS.country}
                        value={condition.operator}
                        onChange={(value) => {
                          const newConditions = [...formData.autoAddRules.conditions];
                          newConditions[index] = {
                            ...newConditions[index],
                            operator: (Array.isArray(value) ? value[0] : value) as RuleOperator,
                          };
                          setFormData((prev) => ({
                            ...prev,
                            autoAddRules: { ...prev.autoAddRules, conditions: newConditions }
                          }));
                        }}
                      />
                    </div>

                    {/* Value input */}
                    {condition.field === 'optedIn' ? (
                      <div className="flex-1">
                        <Select
                          options={OPTED_IN_VALUE_OPTIONS}
                          value={String(condition.value)}
                          onChange={(value) => {
                            const newConditions = [...formData.autoAddRules.conditions];
                            const selectedValue = Array.isArray(value) ? value[0] : value;
                            newConditions[index] = {
                              ...newConditions[index],
                              value: selectedValue,
                            };
                            setFormData((prev) => ({
                              ...prev,
                              autoAddRules: { ...prev.autoAddRules, conditions: newConditions }
                            }));
                          }}
                        />
                      </div>
                    ) : condition.operator === 'in' || condition.operator === 'not_in' ? (
                      <Input
                        placeholder="value1, value2, value3"
                        value={(condition.values || []).join(', ')}
                        onChange={(e) => {
                          const newConditions = [...formData.autoAddRules.conditions];
                          newConditions[index] = {
                            ...newConditions[index],
                            values: e.target.value.split(',').map(v => v.trim()).filter(Boolean),
                          };
                          setFormData((prev) => ({
                            ...prev,
                            autoAddRules: { ...prev.autoAddRules, conditions: newConditions }
                          }));
                        }}
                        className="flex-1"
                      />
                    ) : (
                      <Input
                        placeholder={condition.field === 'country' ? 'e.g. US, GB' : condition.field === 'tags' ? 'e.g. VIP' : 'Enter value'}
                        value={String(condition.value || '')}
                        onChange={(e) => {
                          const newConditions = [...formData.autoAddRules.conditions];
                          newConditions[index] = {
                            ...newConditions[index],
                            value: e.target.value,
                          };
                          setFormData((prev) => ({
                            ...prev,
                            autoAddRules: { ...prev.autoAddRules, conditions: newConditions }
                          }));
                        }}
                        className="flex-1"
                      />
                    )}

                    {/* Remove button */}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        const newConditions = formData.autoAddRules.conditions.filter((_, i) => i !== index);
                        setFormData((prev) => ({
                          ...prev,
                          autoAddRules: { ...prev.autoAddRules, conditions: newConditions }
                        }));
                      }}
                      className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))}

                {/* Add Rule Button */}
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    const newCondition: RuleCondition = {
                      field: 'country',
                      operator: 'equals',
                      value: '',
                    };
                    setFormData((prev) => ({
                      ...prev,
                      autoAddRules: {
                        ...prev.autoAddRules,
                        conditions: [...prev.autoAddRules.conditions, newCondition]
                      }
                    }));
                  }}
                  className="w-full border-dashed"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Rule
                </Button>
              </div>

              {/* Rules Summary */}
              {formData.autoAddRules.conditions.length > 0 && (
                <div className="mt-4 p-3 rounded-lg bg-purple-500/10 border border-purple-500/30">
                  <p className="text-xs text-purple-300">
                    <strong>Summary:</strong> Contacts will be added when{' '}
                    {formData.autoAddRules.operator === 'AND' ? 'ALL' : 'ANY'} of these{' '}
                    {formData.autoAddRules.conditions.length} rule(s) match.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Color Picker */}
          <div>
            <label className="flex items-center gap-1.5 text-sm font-medium text-(--color-soft-gray) mb-3">
              <Palette className="w-4 h-4" />
              Group Color
            </label>
            <div className="flex flex-wrap gap-2">
              {COLOR_OPTIONS.map((color) => (
                <button
                  key={color.value}
                  type="button"
                  onClick={() => setFormData((prev) => ({ ...prev, color: color.value }))}
                  className={`w-8 h-8 rounded-lg transition-all ${
                    formData.color === color.value
                      ? 'ring-2 ring-white ring-offset-2 ring-offset-[#0B1120] scale-110'
                      : 'hover:scale-105'
                  }`}
                  style={{ backgroundColor: color.value }}
                  title={color.label}
                />
              ))}
            </div>
          </div>

          {/* Default Group Toggle */}
          <div className="flex items-center justify-between p-4 rounded-lg bg-white/5 border border-white/10">
            <div className="flex items-center gap-3">
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${formData.type === 'DYNAMIC' ? 'bg-yellow-500/10' : 'bg-yellow-500/20'}`}>
                <Star className={`w-4 h-4 ${formData.type === 'DYNAMIC' ? 'text-yellow-400/50' : 'text-yellow-400'}`} />
              </div>
              <div>
                <p className={`text-sm font-medium ${formData.type === 'DYNAMIC' ? 'text-white/50' : 'text-white'}`}>Default Group</p>
                <p className="text-xs text-(--color-muted-gray)">
                  {formData.type === 'DYNAMIC' 
                    ? 'Dynamic groups cannot be set as default' 
                    : 'New contacts will be automatically added to this group'}
                </p>
              </div>
            </div>
            <Checkbox
              checked={formData.isDefault}
              onChange={(e) => setFormData((prev) => ({ ...prev, isDefault: e.target.checked }))}
              disabled={formData.type === 'DYNAMIC'}
            />
          </div>

          {/* Preview */}
          <div className="p-4 rounded-lg bg-white/5 border border-white/10">
            <p className="text-xs text-(--color-muted-gray) mb-3">Preview</p>
            <div className="flex items-center gap-3">
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center"
                style={{ backgroundColor: `${formData.color}15` }}
              >
                <FolderOpen className="w-6 h-6" style={{ color: formData.color }} />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-sm font-medium text-white">
                    {formData.name || 'Group Name'}
                  </p>
                  <StatusBadge status={formData.status} />
                  <TypeBadge type={formData.type} />
                  {formData.isDefault && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-yellow-500/10 text-yellow-400 border border-yellow-500/30">
                      <Star className="w-3 h-3" />
                      Default
                    </span>
                  )}
                </div>
                {formData.description && (
                  <p className="text-xs text-(--color-muted-gray) mt-1">
                    {formData.description}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Modal Error */}
        {modalError && (
          <div className="mx-6 mb-4 flex items-center gap-3 p-3 rounded-lg bg-red-500/10 border border-red-500/30">
            <AlertCircle className="w-5 h-5 text-red-400 shrink-0" />
            <p className="text-sm text-red-300 flex-1">{modalError}</p>
            <button onClick={() => setModalError(null)} className="text-red-400 hover:text-red-300">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-(--color-border-gray)">
          <Button variant="secondary" onClick={handleCloseModal} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>{editingGroup ? 'Update Group' : 'Create Group'}</>
            )}
          </Button>
        </div>
      </Modal>

      {/* Delete Confirmation */}
      <ConfirmDialog
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        onConfirm={handleDelete}
        title="Delete Group"
        description={`Are you sure you want to delete the "${deleteConfirm?.name}" group? Contacts in this group will not be deleted, but they will be removed from this group. This action cannot be undone.`}
        confirmText="Delete Group"
        variant="danger"
        isLoading={deleting}
      />

      {/* Status Toggle Confirmation */}
      <ConfirmDialog
        isOpen={!!toggleConfirm}
        onClose={() => setToggleConfirm(null)}
        onConfirm={confirmToggleStatus}
        title={toggleConfirm?.status === 'ACTIVE' ? 'Deactivate Group' : 'Activate Group'}
        description={
          toggleConfirm?.status === 'ACTIVE'
            ? `Are you sure you want to deactivate the "${toggleConfirm?.name}" group? Deactivated groups will not be visible in contact forms and filters.`
            : `Are you sure you want to activate the "${toggleConfirm?.name}" group? This will make it visible in contact forms and filters.`
        }
        confirmText={toggleConfirm?.status === 'ACTIVE' ? 'Deactivate' : 'Activate'}
        variant={toggleConfirm?.status === 'ACTIVE' ? 'warning' : 'success'}
        isLoading={toggling}
      />

      {/* View Members Modal */}
      <ViewMembersModal
        isOpen={!!viewingMembersGroup}
        onClose={() => setViewingMembersGroup(null)}
        groupId={viewingMembersGroup?.id || ''}
        onMembersChanged={fetchGroups}
      />

      {/* Export Group Members Modal */}
      <ExportGroupMembersModal
        isOpen={!!exportingGroup}
        onClose={() => setExportingGroup(null)}
        group={exportingGroup}
      />
    </div>
  );
};

export default GroupsPage;
